import {ApiClient as Api} from '@hconnect/apiclient'
import {useNotification} from '@hconnect/uikit'
import {useMutation, UseMutationOptions, useQueryClient} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {useCallback} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'
import {KpiTabGroupDefinitionPayloadDto, SlaTrackingQueryKey} from '../../types'

type SaveKpiDefinitionsParams = {
  plantUpmId: string
}

/**
 * Hook to save KPI definitions (groups and KPIs)
 * POST /sla-kpi-tracking/api/{plantUpmId}/sla-kpi/kpi-definitions
 *
 * The payload contains the full state of all tab groups with their definitions.
 * Backend uses ID matching for change detection:
 * - Existing ID = update
 * - null ID = add (future feature)
 * - Missing from payload = delete (future feature)
 */
export const useSaveKpiDefinitions = (
  {plantUpmId}: SaveKpiDefinitionsParams,
  options?: UseMutationOptions<void, AxiosError, KpiTabGroupDefinitionPayloadDto[]>
) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const {notify} = useNotification()
  const queryClient = useQueryClient()

  const mutationFn: UseMutationOptions<
    void,
    AxiosError,
    KpiTabGroupDefinitionPayloadDto[]
  >['mutationFn'] = useCallback(
    async (data: KpiTabGroupDefinitionPayloadDto[]) => {
      await Api.axiosInstance.post(
        `/sla-kpi-tracking/api/${plantUpmId}/sla-kpi/kpi-definitions`,
        data
      )
    },
    [plantUpmId]
  )

  return useMutation<void, AxiosError<{message?: string}>, KpiTabGroupDefinitionPayloadDto[]>({
    mutationFn,
    onError: (error) => {
      const errorMessage = error.response?.data?.message ?? t('editKpisModal.message.saveError')

      notify('error', errorMessage)
    },
    onSuccess: async (...args) => {
      notify('success', t('editKpisModal.message.saveSuccess'))
      await options?.onSuccess?.(...args)
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: [SlaTrackingQueryKey.SlaKpiTabValues, plantUpmId]
      })
    },
    ...options
  })
}
